import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Lock } from "@phosphor-icons/react";
import RotatingWord from "./RotatingWord";

/**
 * Editorial category-group card — elegant, animated, premium.
 * Refined border, soft shadow, animated underline, hover reveal,
 * shimmering "Coming Soon" state for Beauty Devices.
 */
export default function GroupCard({ group, count = 0, onClick, index = 0 }) {
  const { name, tagline, color, subs = [], comingSoon } = group;
  const subNames = subs.map((s) => s.name);
  const [hover, setHover] = useState(false);

  return (
    <motion.button
      data-testid={`group-card-${group.slug}`}
      onClick={comingSoon ? undefined : onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: index * 0.08 }}
      whileHover={comingSoon ? undefined : { y: -8 }}
      className={`group relative text-left overflow-hidden rounded-[28px] bg-white p-7 md:p-8 min-h-[320px] w-full border border-slate-200/80 shadow-[0_2px_18px_rgba(15,23,42,0.04)] transition-shadow duration-500 ${
        comingSoon ? "" : "hover:shadow-[0_30px_70px_-25px_rgba(15,23,42,0.20)]"
      }`}
    >
      {/* Inner stroke / animated outline */}
      <motion.div
        aria-hidden
        className="absolute inset-0 rounded-[28px] pointer-events-none"
        animate={hover && !comingSoon ? { boxShadow: "inset 0 0 0 1.5px rgba(15,23,42,0.85)" } : { boxShadow: "inset 0 0 0 0px rgba(15,23,42,0)" }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      />

      {/* Gradient bloom — wider, slower, more elegant */}
      <motion.div
        aria-hidden
        className={`absolute -top-24 -right-24 w-72 h-72 rounded-full bg-gradient-to-br ${color} blur-3xl`}
        animate={{
          opacity: hover && !comingSoon ? 0.85 : 0.45,
          scale: hover && !comingSoon ? 1.1 : 1,
        }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      />

      <div className="relative flex flex-col h-full">
        {/* top row */}
        <div className="flex items-start justify-between">
          <span className="font-display text-[10px] tracking-[0.28em] uppercase text-slate-400">
            Nº 0{index + 1}
          </span>
          {comingSoon ? (
            <span
              data-testid="coming-soon-badge"
              className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase font-medium bg-[#0F172A] text-white px-2.5 py-1 rounded-full"
            >
              <Lock size={10} weight="bold" /> Coming Soon
            </span>
          ) : (
            <span className="text-[10px] tracking-[0.22em] uppercase text-slate-400">
              {count} {count === 1 ? "drop" : "drops"}
            </span>
          )}
        </div>

        {/* Name + animated underline */}
        <div className="mt-10 relative inline-block">
          <h3 className="font-display text-3xl md:text-[2.6rem] font-light tracking-[-0.03em] leading-[1.02]">
            {name}
          </h3>
          <motion.span
            aria-hidden
            className="absolute left-0 -bottom-1 h-[2px] bg-[#0F172A]"
            initial={{ width: "0%" }}
            animate={{ width: hover && !comingSoon ? "60%" : "0%" }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          />
        </div>
        <p className="mt-3 text-[12px] tracking-[0.18em] uppercase text-slate-500">{tagline}</p>

        {/* Rotating sub-label */}
        <div className="mt-5 text-[15px] md:text-base font-medium text-slate-700">
          <RotatingWord
            words={subNames.length ? subNames : [name]}
            interval={2600}
            stagger={0.025}
            duration={0.55}
          />
        </div>

        {/* sub chips — slide in on hover */}
        <div className="mt-auto pt-7 flex flex-wrap gap-1.5">
          {subs.map((s, i) => (
            <motion.span
              key={`${s.slug}-${s.name}-${i}`}
              initial={{ opacity: 0, y: 6 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 + i * 0.07, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="text-[10px] tracking-[0.22em] uppercase px-2.5 py-1 rounded-full border border-slate-200 text-slate-600 group-hover:border-slate-900 group-hover:text-slate-900 transition-colors duration-500"
            >
              {s.name}
            </motion.span>
          ))}
        </div>

        {/* CTA row */}
        <div className="mt-7 flex items-center justify-between">
          <span className="text-[10px] tracking-[0.28em] uppercase text-slate-400">
            {comingSoon ? "On the way" : "Explore"}
          </span>
          <motion.span
            animate={{
              backgroundColor: comingSoon ? "#F1F5F9" : (hover ? "#FF6B9E" : "#0F172A"),
              rotate: hover && !comingSoon ? 45 : 0,
              scale: hover && !comingSoon ? 1.08 : 1,
            }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className={`w-11 h-11 rounded-full flex items-center justify-center ${comingSoon ? "text-slate-400" : "text-white"}`}
          >
            {comingSoon ? <Lock size={14} weight="bold" /> : <ArrowUpRight size={14} weight="bold" />}
          </motion.span>
        </div>
      </div>

      {/* Bottom accent line that draws in on hover */}
      <motion.div
        aria-hidden
        className={`absolute left-0 right-0 bottom-0 h-[3px] bg-gradient-to-r ${color}`}
        initial={{ scaleX: 0, originX: 0 }}
        animate={{ scaleX: hover && !comingSoon ? 1 : 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        style={{ transformOrigin: "left center" }}
      />
    </motion.button>
  );
}
